/**
 * 全系統唯一的模型價格表。前後端共用。
 *
 * 為什麼放在 src/shared/：後端計費（config/pricing.ts）與後台成本顯示
 * （ui/admin/costUtils.ts）原本各有一份，且匯率不同（33 vs 32），
 * 導致同一筆紀錄在扣點與顯示上金額不一致。先例：src/shared/flowModel.ts。
 *
 * 修改本檔不會追溯改寫歷史扣點 —— Wallet.pointLogs 存的是當時的單價快照。
 * 但會即時改變後台 GenerationRecords / AiCallLogs 的成本顯示，那兩頁是
 * 用 modelName 即時查表換算的。
 */

/** 美金兌台幣。寫死值，非即時匯率；是否調整屬商業決策（spec §8）。 */
export const USD_TO_TWD = 33;

export interface ModelPricing {
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  thoughtUsdPerMillion: number;
}

/**
 * 各模型定價（USD / 每百萬 token）。
 *
 * 標「歷史」的 key 已無呼叫端，但仍存在於 GenerationRecords / AiCallLogs
 * 的既有資料中 —— 移除會讓那些紀錄的成本欄變成「—」，故一律保留。
 *
 * thought 一律等於 output：Gemini 官方價目表的欄位標題為
 * "Output price (including thinking tokens)"，推理 token 不獨立計價。
 */
export const MODEL_PRICING: Record<string, ModelPricing> = {
  // 現役
  'gemini-3.1-flash-lite':          { inputUsdPerMillion: 0.25, outputUsdPerMillion: 1.50, thoughtUsdPerMillion: 1.50 },
  'gemini-3.5-flash-lite':          { inputUsdPerMillion: 0.30, outputUsdPerMillion: 2.50, thoughtUsdPerMillion: 2.50 },
  'gemini-3.1-flash-image':         { inputUsdPerMillion: 0.50, outputUsdPerMillion: 60.0, thoughtUsdPerMillion: 3.00 },

  // 歷史 —— 僅供既有紀錄的成本顯示，勿移除
  'gemini-3.1-flash-lite-preview':  { inputUsdPerMillion: 0.25, outputUsdPerMillion: 1.50, thoughtUsdPerMillion: 1.50 },
  'gemini-3.1-flash-image-preview': { inputUsdPerMillion: 0.50, outputUsdPerMillion: 60.0, thoughtUsdPerMillion: 3.00 },
  'gemini-3-flash-preview':         { inputUsdPerMillion: 0.50, outputUsdPerMillion: 3.00, thoughtUsdPerMillion: 3.00 },
  'gemini-3-pro-image-preview':     { inputUsdPerMillion: 2.00, outputUsdPerMillion: 120.0, thoughtUsdPerMillion: 12.0 },
  'gemini-2.5-flash-lite':          { inputUsdPerMillion: 0.10, outputUsdPerMillion: 0.40, thoughtUsdPerMillion: 0.40 },
  'gemini-2.5-flash':               { inputUsdPerMillion: 0.30, outputUsdPerMillion: 2.50, thoughtUsdPerMillion: 2.50 },
};

/** 查不到單價時的保底費率，刻意偏高以免低估成本。 */
export const DEFAULT_PRICING: ModelPricing = {
  inputUsdPerMillion: 0.15,
  outputUsdPerMillion: 0.60,
  thoughtUsdPerMillion: 3.50,
};

export function getModelPricing(modelName: string): ModelPricing {
  return MODEL_PRICING[modelName] ?? DEFAULT_PRICING;
}
